import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { usePermission } from "../../../features/auth/use-permission";
import { HistoryTabs } from "../../../features/change-history/components/history-tabs";
import { RecordHistory } from "../../../features/change-history/components/record-history";
import { historyTabSearchSchema } from "../../../features/change-history/history-search";
import { standingCcsQueryOptions } from "../../../features/standing-copies/api/standing-copies";
import { StandingCopyAdmin } from "../../../features/standing-copies/components/standing-copy-admin";

/**
 * `RPM-044`'s standing-copy administration list (`J-282`), with each copy's History (`RPM-063`,
 * `J-291`). The copies are the tenant's, so there is no building to choose and no building's zone:
 * the history's times are shown in UTC and say so. The tab is rendered only for an account holding
 * `audit: ["read"]`; the server decides on every request.
 */
function StandingCopiesScreen() {
  const { tab = "details" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const canReadHistory = usePermission({ audit: ["read"] });
  // The copies are tenant-wide, so only a tenant assignment changes them (`standing-ccs.routes.ts`).
  const canChange = usePermission({ routing: ["update"] }, ["TENANT"]);
  const copies = useQuery({ ...standingCcsQueryOptions(), enabled: canReadHistory });
  return (
    <HistoryTabs
      showHistory={canReadHistory}
      tab={tab}
      onTabChange={(next) =>
        void navigate({ search: (prev) => ({ ...prev, tab: next }), replace: true })
      }
      detailsLabel="Standing copies"
      details={<StandingCopyAdmin readOnly={!canChange} />}
      history={
        <RecordHistory
          entity="standing-ccs"
          records={(copies.data ?? []).map((c) => ({
            id: c.standingCcId,
            label: `${c.label} (${c.address})`,
          }))}
          noun="Standing copy"
          caption={(label) => `Change history of standing copy ${label}`}
        />
      }
    />
  );
}

export const Route = createFileRoute("/_authenticated/admin/standing-copies")({
  validateSearch: historyTabSearchSchema,
  component: StandingCopiesScreen,
  staticData: { screenName: "Standing copies" },
});
